import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [successVisible, setSuccessVisible] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSavingForm, setIsSavingForm] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    street: "",
    zone: "",
    barangay: "",
    city: "",
    phone_number: "",
    password: ""
  });

  const loadAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getAdmins();
      setAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load admins:", e);
      setError("Failed to load admins.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (admin) => {
    setError(null);
    setSuccess(null);
    setIsLoadingEdit(true);
    try {
      const details = await ApiService.getAdminById(admin.admin_id);
      setForm({
        first_name: details.first_name || "",
        last_name: details.last_name || "",
        street: details.street || "",
        zone: details.zone || "",
        barangay: details.barangay || "",
        city: details.city || "",
        phone_number: details.phone_number || "",
        password: ""
      });
      setEditingAdmin(admin);
      setShowCreateForm(false);
    } catch (e) {
      console.error("Load admin details failed:", e);
      setError(e?.message || "Failed to load admin details.");
    } finally {
      setIsLoadingEdit(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  useEffect(() => {
    if (!success) {
      setSuccessVisible(false);
      return;
    }

    setSuccessVisible(true);
    const fadeTimer = window.setTimeout(() => setSuccessVisible(false), 2500);
    const clearTimer = window.setTimeout(() => setSuccess(null), 3000);

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(clearTimer);
    };
  }, [success]);

  const resetForm = () => {
    setForm({
      first_name: "",
      last_name: "",
      street: "",
      zone: "",
      barangay: "",
      city: "",
      phone_number: "",
      password: ""
    });
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingAdmin(null);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (adminId) => {
    const confirm = window.confirm('Delete this admin? This action cannot be undone.');
    if (!confirm) return;
    try {
      setDeletingId(adminId);
      await ApiService.deleteAdmin(adminId);
      setSuccess('Admin deleted successfully.');
      await loadAdmins();
    } catch (e) {
      console.error('Delete admin failed:', e);
      setError(e?.message || 'Failed to delete admin.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Basic validations
    if (!form.first_name || !form.last_name || !form.phone_number || (!editingAdmin && !form.password)) {
      setError(editingAdmin
        ? "Please fill out first name and last name."
        : "Please fill out first name, last name, phone number, and password.");
      return;
    }
    if (!editingAdmin && form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    try {
      setIsSavingForm(true);
      if (editingAdmin) {
        await ApiService.updateAdmin(editingAdmin.admin_id, {
          first_name: form.first_name,
          last_name: form.last_name,
          street: form.street,
          zone: form.zone,
          barangay: form.barangay,
          city: form.city
        });
        setSuccess("Admin updated successfully.");
      } else {
        await ApiService.createAdmin(form);
        setSuccess("Admin created successfully.");
      }
      closeForm();
      await loadAdmins();
    } catch (e) {
      console.error(editingAdmin ? "Update admin failed:" : "Create admin failed:", e);
      setError(e?.message || (editingAdmin ? "Failed to update admin." : "Failed to create admin."));
    } finally {
      setIsSavingForm(false);
    }
  };

  return (
    <Layout>
      <style>{`
        @media (max-width: 767px) {
          .admin-table-wrapper {
            display: none !important;
          }
          .admin-cards-wrapper {
            display: grid !important;
            gap: 12px;
          }
          .admin-create-modal {
            max-width: 100% !important;
            width: 100% !important;
            padding: 18px !important;
          }
          .admin-form-grid {
            grid-template-columns: 1fr !important;
          }
        }

        @media (min-width: 768px) {
          .admin-cards-wrapper {
            display: none !important;
          }
        }
      `}</style>
      <div>
        <div style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          borderRadius: '16px',
          padding: '24px 28px',
          marginBottom: '24px',
          boxShadow: '0 8px 24px rgba(37, 99, 235, 0.16)',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '28px', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                <span style={{
                  marginRight: '12px',
                  width: '40px',
                  height: '40px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.18)',
                  borderRadius: '10px'
                }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </span>
                Admin Management
              </h2>
              <p style={{ margin: '8px 0 0 0', fontSize: '15px', opacity: 0.95 }}>Create and oversee admin accounts for the eLaba platform.</p>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: 0.9 }}>Total Admins: {admins.length}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { closeForm(); setShowCreateForm(true); }} style={{
                backgroundColor: '#fff',
                border: 'none',
                color: '#1d4ed8',
                padding: '11px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.14)'
              }}>+ Add New Admin</button>
            </div>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#b91c1c',
            padding: '12px 16px',
            borderRadius: '10px',
            marginBottom: '16px',
            boxShadow: '0 2px 8px rgba(185, 28, 28, 0.08)'
          }}>{error}</div>
        )}
        {success && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '20px',
            zIndex: 1000,
            pointerEvents: 'none'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
              border: '1px solid #86efac',
              color: '#166534',
              padding: '16px 22px',
              borderRadius: '16px',
              boxShadow: '0 14px 34px rgba(6, 95, 70, 0.2)',
              minWidth: '320px',
              maxWidth: '480px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              opacity: successVisible ? 1 : 0,
              transform: successVisible ? 'translateY(0)' : 'translateY(-8px)',
              transition: 'all 0.35s ease'
            }}>
              <div style={{ fontSize: '22px', lineHeight: 1 }}>✓</div>
              <div>
                <div style={{ fontWeight: 800, marginBottom: '3px', fontSize: '16px' }}>Success</div>
                <div style={{ fontSize: '15px' }}>{success}</div>
              </div>
            </div>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {!showCreateForm && !editingAdmin ? (
            <div style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)', borderRadius: '16px', padding: '22px', boxShadow: '0 4px 16px rgba(15, 23, 42, 0.07)', border: '1px solid #e2e8f0' }}>
              <div style={{ marginBottom: 12, paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, color: '#1e293b' }}>Existing Admins</h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>Monitor current admin accounts and remove access when needed.</p>
              </div>
              {loading ? (
                <p style={{ color: '#64748b' }}>Loading admins...</p>
              ) : admins.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: '24px' }}>No admins found.</div>
              ) : (
                <>
                  <div className="admin-table-wrapper" style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8fafc', textAlign: 'left' }}>
                          <th style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Name</th>
                          <th style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Phone</th>
                          <th style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>City</th>
                          <th style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>Created</th>
                          <th style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {admins.map((a) => (
                          <tr key={a.admin_id}>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>{a.first_name} {a.last_name}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>{a.phone_number}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>{a.city || '-'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                              <button
                                type="button"
                                onClick={() => handleEdit(a)}
                                disabled={isLoadingEdit}
                                style={{
                                  backgroundColor: '#dbeafe',
                                  border: '1px solid #bfdbfe',
                                  color: '#1d4ed8',
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  cursor: isLoadingEdit ? 'not-allowed' : 'pointer',
                                  fontWeight: 600,
                                  marginRight: 8
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(a.admin_id)}
                                disabled={deletingId === a.admin_id}
                                style={{
                                  backgroundColor: '#fee2e2',
                                  border: '1px solid #fecaca',
                                  color: '#b91c1c',
                                  padding: '8px 10px',
                                  borderRadius: 10,
                                  cursor: deletingId === a.admin_id ? 'not-allowed' : 'pointer',
                                  fontWeight: 600
                                }}
                              >
                                {deletingId === a.admin_id ? 'Deleting...' : 'Delete'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="admin-cards-wrapper" style={{ display: 'none' }}>
                    {admins.map((a) => (
                      <div key={a.admin_id} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Admin</div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              onClick={() => handleEdit(a)}
                              disabled={isLoadingEdit}
                              style={{
                                backgroundColor: '#dbeafe',
                                border: '1px solid #bfdbfe',
                                color: '#1d4ed8',
                                padding: '7px 10px',
                                borderRadius: 8,
                                cursor: isLoadingEdit ? 'not-allowed' : 'pointer',
                                fontWeight: 600,
                                fontSize: 12
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(a.admin_id)}
                              disabled={deletingId === a.admin_id}
                              style={{
                                backgroundColor: '#fee2e2',
                                border: '1px solid #fecaca',
                                color: '#b91c1c',
                                padding: '7px 10px',
                                borderRadius: 8,
                                cursor: deletingId === a.admin_id ? 'not-allowed' : 'pointer',
                                fontWeight: 600,
                                fontSize: 12
                              }}
                            >
                              {deletingId === a.admin_id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gap: 10 }}>
                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Name</div>
                            <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{a.first_name} {a.last_name}</div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Phone</div>
                              <div style={{ fontSize: 13, color: '#334155' }}>{a.phone_number || '-'}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>City</div>
                              <div style={{ fontSize: 13, color: '#334155' }}>{a.city || '-'}</div>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Created</div>
                            <div style={{ fontSize: 13, color: '#334155' }}>{a.created_at ? new Date(a.created_at).toLocaleString() : '-'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)', borderRadius: '16px', padding: '22px', boxShadow: '0 4px 16px rgba(15, 23, 42, 0.07)', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '18px' }}>
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: '4px', color: '#1e293b' }}>{editingAdmin ? 'Edit Admin Account' : 'Add New Admin'}</h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '13px' }}>{editingAdmin ? 'Update the selected admin account details.' : 'Fill in the details below to create a new admin account.'}</p>
                </div>
                <button type="button" aria-label={`Close ${editingAdmin ? 'edit' : 'add'} admin form`} onClick={closeForm} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', color: '#64748b', cursor: 'pointer', fontSize: '22px', lineHeight: 1, padding: '5px 9px' }}>×</button>
              </div>

              {isLoadingEdit ? <p style={{ color: '#64748b' }}>Loading admin details...</p> : <form onSubmit={handleSubmit}>
                {editingAdmin && <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>Update the admin details. Phone number is locked because it is used for login.</p>}
                <div className="admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>First Name</label>
                    <input name="first_name" value={form.first_name} onChange={handleChange} required placeholder="Juan" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Last Name</label>
                    <input name="last_name" value={form.last_name} onChange={handleChange} required placeholder="Dela Cruz" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Street</label>
                    <input name="street" value={form.street} onChange={handleChange} placeholder="123 Sample St" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Zone</label>
                    <input name="zone" value={form.zone} onChange={handleChange} placeholder="Zone 1" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Barangay</label>
                    <input name="barangay" value={form.barangay} onChange={handleChange} placeholder="Barangay 123" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>City</label>
                    <input name="city" value={form.city} onChange={handleChange} placeholder="Quezon City" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Phone Number</label>
                    <input name="phone_number" value={form.phone_number} onChange={handleChange} required placeholder="09xxxxxxxxx" readOnly={!!editingAdmin} style={editingAdmin ? { ...inputStyle, color: '#64748b', cursor: 'not-allowed' } : inputStyle} />
                  </div>
                  {!editingAdmin && <div>
                    <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 6 }}>Password</label>
                    <input type="password" name="password" value={form.password} onChange={handleChange} required placeholder="••••••••" style={inputStyle} />
                  </div>}
                </div>
                <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="submit" disabled={isSavingForm} style={{ ...primaryBtnStyle, cursor: isSavingForm ? 'not-allowed' : 'pointer', opacity: isSavingForm ? 0.7 : 1 }}>{isSavingForm ? (editingAdmin ? 'Saving...' : 'Creating...') : (editingAdmin ? 'Save Changes' : 'Create Admin')}</button>
                  {!editingAdmin && <button type="button" onClick={resetForm} style={secondaryBtnStyle}>Clear</button>}
                  <button type="button" onClick={closeForm} style={{ ...secondaryBtnStyle, backgroundColor: '#fff', color: '#475569' }}>Cancel</button>
                </div>
              </form>}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  borderRadius: '10px',
  border: '1px solid #cbd5e1',
  outline: 'none',
  fontSize: 14,
  color: '#0f172a',
  backgroundColor: '#f8fafc'
};

const primaryBtnStyle = {
  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
  color: 'white',
  border: 'none',
  padding: '11px 16px',
  borderRadius: '10px',
  cursor: 'pointer',
  fontWeight: 700,
  boxShadow: '0 6px 16px rgba(37, 99, 235, 0.2)'
};

const secondaryBtnStyle = {
  backgroundColor: '#f8fafc',
  color: '#1e293b',
  border: '1px solid #cbd5e1',
  padding: '11px 16px',
  borderRadius: '10px',
  cursor: 'pointer',
  fontWeight: 600
};
